// frontend/src/components/InstructionsEditor.js - Updated
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import instructionsService from '../services/instructions/instructionsService';
import '../styles/global.css';

const InstructionsEditor = () => {
    const { projectId } = useParams();
    const [text, setText] = useState('');
    const [originalText, setOriginalText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchInstructions = async () => {
            setLoading(true);
            setError('');
            try {
                const instructionsText = await instructionsService.getInstructions(projectId);
                
                // Set both current and original text for comparison/reset purposes
                setText(instructionsText);
                setOriginalText(instructionsText);
            } catch (err) {
                console.error("Error fetching instructions:", err);
                // Use default instructions if there was an error
                const defaultText = instructionsService.getDefaultInstructions();
                setText(defaultText);
                setOriginalText(defaultText);
            } finally {
                setLoading(false);
            }
        };

        fetchInstructions();
    }, [projectId]);

    const save = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        
        try {
            await instructionsService.updateInstructions(projectId, text);
            setSuccess("Instructions saved successfully");
            // Update original text to match current text after save
            setOriginalText(text);
        } catch (err) {
            console.error("Error saving instructions:", err);
            setError(err.message || "Failed to save instructions. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setText(originalText);
        setError('');
        setSuccess('');
    };

    // Reset to default instructions
    const resetToDefault = () => {
        const defaultText = instructionsService.getDefaultInstructions();
        setText(defaultText);
        setError('');
        setSuccess('');
    };

    return (
        <div className="container">
            <div className="back-button" onClick={() => navigate(`/projects/${projectId}`)}>
                ← Back to Project
            </div>

            <div className="card">
                <h1 className="text-2xl font-bold mb-4">Edit Translation Instructions</h1>
                <p className="mb-4 text-gray-600">
                    Customize the instructions for the AI translator. These instructions will be used for all 
                    translations in this project.
                </p>

                {loading ? (
                    <div className="text-center p-4">
                        <div className="loading">Loading instructions...</div>
                    </div>
                ) : (
                    <>
                        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
                        {success && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{success}</div>}
                        
                        <div className="mb-4">
                            <textarea 
                                value={text} 
                                onChange={e => setText(e.target.value)} 
                                rows="12" 
                                className="form-textarea font-mono text-sm w-full"
                                placeholder="Enter custom translation instructions..."
                            />
                        </div>
                        
                        <div className="flex flex-wrap gap-3">
                            <button 
                                onClick={save}
                                disabled={saving || text === originalText}
                                className="btn btn-primary"
                            >
                                {saving ? (
                                    <span className="flex items-center">
                                        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </span>
                                ) : (
                                    'Save Instructions'
                                )}
                            </button>
                            
                            <button 
                                onClick={reset}
                                disabled={text === originalText}
                                className="btn btn-outline"
                            >
                                Reset
                            </button>
                            
                            <button 
                                onClick={resetToDefault}
                                disabled={text === instructionsService.getDefaultInstructions()}
                                className="btn btn-secondary"
                            >
                                Reset to Default
                            </button>
                        </div>

                        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <h3 className="text-sm font-semibold text-blue-800 mb-2">Tips for Writing Instructions</h3>
                            <ul className="text-sm text-blue-700 space-y-1">
                                <li>• Be specific about formatting requirements</li>
                                <li>• Mention any special terms that should remain untranslated</li>
                                <li>• Provide context about the document's audience</li>
                                <li>• Specify the desired tone and style of the translation</li>
                            </ul>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default InstructionsEditor;